/**
 * A heart toggle that follows/unfollows a marketplace listing. Reads live state from the shared
 * watchlist store and stays in sync with every other instance via its change event.
 */

import { useEffect, useState } from 'react';
import { isWatched, onWatchlistChange, toggleWatch } from '@/lib/watchlist';

export function WatchButton({
  id,
  className,
  onSurface = false,
}: {
  id: string;
  className?: string;
  /** True when placed over a coloured card header, so the resting state reads on a gradient. */
  onSurface?: boolean;
}) {
  const [watched, setWatched] = useState(false);

  useEffect(() => {
    setWatched(isWatched(id));
    return onWatchlistChange(() => setWatched(isWatched(id)));
  }, [id]);

  const base = onSurface
    ? 'bg-white/20 text-white backdrop-blur hover:bg-white/30'
    : 'border border-border bg-surface text-ink-muted hover:text-danger';

  return (
    <button
      type="button"
      aria-pressed={watched}
      aria-label={watched ? 'Remove from watchlist' : 'Add to watchlist'}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setWatched(toggleWatch(id));
      }}
      className={
        className ??
        `flex h-8 w-8 items-center justify-center rounded-full transition ${base} ${watched ? '!text-danger' : ''}`
      }
    >
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill={watched ? 'currentColor' : 'none'}
        stroke="currentColor"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M12 20s-7-4.35-9.2-8.5C1.3 8.6 2.6 5.5 5.7 5.5c1.9 0 3.2 1.1 4.3 2.6 1.1-1.5 2.4-2.6 4.3-2.6 3.1 0 4.4 3.1 2.9 6-2.2 4.15-9.2 8.5-9.2 8.5Z"
        />
      </svg>
    </button>
  );
}
